import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import LeadsActions from "./LeadsActions";
import LeadsDetail from "./LeadsDetail";
import { IoIosArrowForward } from "react-icons/io";
import { useRouteMatch, useHistory } from "react-router-dom";
import { twoDecimalPlaces } from "../../utils/utilsHandlerFunctions";
import { getFullName } from "../../utils/CommonFunctions";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "ProspectName",
    numeric: false,
    disablePadding: true,
    label: "Prospect Name",
  },
  {
    id: "Referral",
    numeric: false,
    disablePadding: true,
    label: "Referral",
  },
  {
    id: "TypeofPlan",
    numeric: false,
    disablePadding: true,
    label: "Type of Plan",
  },
  {
    id: "RateperWeek",
    numeric: false,
    disablePadding: true,
    label: "Rate per Week",
  },
  {
    id: "ContactName",
    numeric: false,
    disablePadding: true,
    label: "Contact Name",
  },
  {
    id: "Tel",
    numeric: false,
    disablePadding: true,
    label: "Tel",
  },
  {
    id: "Email",
    numeric: false,
    disablePadding: true,
    label: "Email",
  },
  {
    id: "SalesPerson",
    numeric: false,
    disablePadding: true,
    label: "Sales Person",
  },
  {
    id: "Client Add Date",
    numeric: false,
    disablePadding: true,
    label: "Client Add Date",
  },
  {
    id: "Status",
    numeric: false,
    disablePadding: true,
    label: "Status",
  },
  {
    id: "redirect",
    numeric: false,
    disablePadding: true,
    label: "",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead
      sx={{
        backgroundColor: "#fff",
        opacity: 1,
        position: "sticky",
        top: 0,
      }}
    >
      <TableRow>
        <TableCell padding="none">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Nutrition
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function LeadsManagmentTable(props) {
  const { rows, leadData, getLeads, leadAccess } = props;
  // const [startDate, endDate] = dateRange;
  const history = useHistory();
  let { url } = useRouteMatch();

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [leadDetail, setLeadDetail] = useState(null);

  // useEffect(() => {
  //   // getLeads();
  // }, []);

  const applyDateFilter = () => {
    getLeads();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n._id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleLeadsDetails = (leadata) => {
    setLeadDetail(leadata);
    if (leadata) {
      history.replace({ pathname: `${url}/details` });
    } else {
      history.replace({ pathname: `${url}` });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <>
      {leadDetail ? (
        <LeadsDetail
          leadDetail={leadDetail}
          handleLeadsDetails={handleLeadsDetails}
          getLeads={getLeads}
        />
      ) : (
        <>
          <LeadsActions
            leadAccess={leadAccess}
            applyDateFilter={applyDateFilter}
            leadsId={selected}
            leadData={leadData}
            rowData={rows}
          />
          <Box sx={{ width: "100%" }}>
            <Paper sx={{ width: "100%" }}>
              {/* <EnhancedTableToolbar numSelected={selected.length} /> */}
              <TableContainer sx={{ height: "55vh" }}>
                <Table
                  sx={{ minWidth: 750 }}
                  aria-labelledby="tableTitle"
                  size={"small"}
                >
                  <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={rows.length}
                  />
                  <TableBody>
                    {stableSort(rows, getComparator(order, orderBy))
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        const isItemSelected = isSelected(row._id);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row._id}
                            selected={isItemSelected}
                          >
                            <TableCell padding="none">
                              <Checkbox
                                color="primary"
                                onClick={(event) => handleClick(event, row._id)}
                                checked={isItemSelected}
                                inputProps={{
                                  "aria-labelledby": labelId,
                                }}
                              />
                            </TableCell>
                            <TableCell
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="none"
                            >
                              {getFullName(row)}
                              {/* {row.first_Name + " " + row.last_Name} */}
                            </TableCell>
                            <TableCell padding="none">{row.referral}</TableCell>
                            <TableCell padding="none">
                              {row.clientPlanType}
                            </TableCell>
                            <TableCell padding="none">
                              {twoDecimalPlaces(row.invoiceRate)}
                            </TableCell>
                            <TableCell padding="none">
                              {row.keycontact[0]?.name}
                            </TableCell>
                            <TableCell padding="none">
                              {row.keycontact[0]?.phone}
                            </TableCell>
                            <TableCell padding="none">
                              {row.keycontact[0]?.email}
                            </TableCell>
                            <TableCell padding="none">
                              {row.salesPerson}
                            </TableCell>
                            <TableCell padding="none">
                              {row.addedTimestamp
                                ? row.addedTimestamp.split("T")[0]
                                : "--"}
                            </TableCell>
                            <TableCell>
                              <button
                                className={`btn status_btn ${row.leadsStatus === "success"
                                    ? "btn-success"
                                    : ""
                                  }
                                                            ${row.leadsStatus ===
                                    "earlyStage"
                                    ? "btn-secondary"
                                    : ""
                                  }
                                                            ${row.leadsStatus ===
                                    "qualified"
                                    ? "btn-primary"
                                    : ""
                                  }
                                                            ${row.leadsStatus ===
                                    "caseClosed"
                                    ? "btn-danger"
                                    : ""
                                  }
                                  ${row.leadsStatus === "Added to client"
                                    ? "btn-warning"
                                    : ""
                                  }`}
                              >
                                {row.leadsStatus === "earlyStage"
                                  ? "Early Stage"
                                  : row.leadsStatus === "caseClosed"
                                    ? "Case Closed"
                                    : row.leadsStatus}
                              </button>
                            </TableCell>
                            <TableCell>
                              <IoIosArrowForward
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => handleLeadsDetails(row)}
                              />
                              {/* <BsThreeDots /> */}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows,
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
